export const properties = {
    /**
     * HTO Allocation API error messages.
     *
     * Structure:
     *  - Top-level keys are HTTP status codes or 'default'.
     *  - For status 422, the value is an object keyed by the error code string
     *    found in the response body (errors[0].errorCode), with a 'default' fallback.
     *  - For all other status codes the value is the user-facing string directly.
     *  - 'default' top-level key is the final fallback for any unmapped status.
     *
     * To add a new case: add a new numeric key (or a new errorCode key under 422).
     */
    htoAllocationErrors: {
        429: "We're having trouble right now. Please try scanning again in a moment.",
        500: "This QR code couldn't be applied. Please scan again in a moment.",
        422: {
            // store provided is not in the store_list of the offer
            JUAL4223E: "This deal isn't available at your selected store. Try choosing a different store.",
            // offer is not active or has expired
            JUAL4222E: 'This deal is no longer available. Browse more.',
            // clip limit is reached for the offer
            JUAL4226E: 'This deal is no longer available. Browse more.',
            // offer is not within display effective date range
            JUAL4225E: 'This deal is no longer available. Browse more.',
            // offer is redeemed already by the user
            JUAL4227E: 'This deal is no longer available. Browse more.',
            default: "This request can't be completed.",
        },
        default: "This request can't be completed.",
    } as Record<number | 'default', string | Record<string, string>>,
    oktaRedirectUri: '/osar/msi',
    oktaRedirectUriImplict: '/osar/msi/implicit/callback',
    oktaRedirectUriConfirmation: '/erums/order/confirmation/implicit/callback',
    erumsRedirectCookieName: 'erums-from-uri',
    clientBannerHeader: 'banner',
    headerStoreId: '',
    headerFulfillment: 'instore',
    storeId: '',
    discoveryXERFeatureID: 'ACIP548280',
    bannerConfiguration: {
        tomthumb: {
            banner: 'Tom Thumb',
            loyaltyPgmName: 'Reward Card',
            promoDescription: 'Reward Card Price',
            estimatedSavings: 'Est. Reward Card Savings',
            adaEstimatedSavings: 'Estimated Reward Card Savings',
            logoUrls: {
                color: 'TomThumb_RGB.svg',
                white: 'TomThumb_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://tomthumbqa.onelink.me/4O7g/f93xacn9',
            nonProdshareAdEmailUrl: 'https://tomthumbqa.onelink.me/4O7g/51n26qqp',
            prodshareAdCopyUrl: 'https://tomthumb.onelink.me/wBO4/ohnrm9ly',
            prodshareAdEmailUrl: 'https://tomthumb.onelink.me/wBO4/3zct8474',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        albertsons: {
            banner: 'Albertsons',
            loyaltyPgmName: 'On Sale',
            promoDescription: 'On Sale Now',
            estimatedSavings: 'Est. On Sale Savings',
            adaEstimatedSavings: 'Estimated On Sale Savings',
            logoUrls: {
                color: 'Albertsons_RGB.svg',
                white: 'Albertsons_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://albertsonsqa.onelink.me/PMmj/oasskgk6',
            nonProdshareAdEmailUrl: 'https://albertsonsqa.onelink.me/PMmj/s8t0ytn6',
            prodshareAdCopyUrl: 'https://albertsons.onelink.me/eT3h/1a3qb2j6',
            prodshareAdEmailUrl: 'https://albertsons.onelink.me/eT3h/w7skqc2u',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        vons: {
            banner: 'Vons',
            loyaltyPgmName: 'Club Card',
            promoDescription: 'Club Card Price',
            estimatedSavings: 'Est. Club Card Savings',
            adaEstimatedSavings: 'Estimated Club Card Savings',
            logoUrls: {
                color: 'Vons_RGB.svg',
                white: 'Vons_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://vonsqa.onelink.me/A8Yt/n8in2xgg',
            nonProdshareAdEmailUrl: 'https://vonsqa.onelink.me/A8Yt/ywc5nhf6',
            prodshareAdCopyUrl: 'https://vons.onelink.me/MuHG/37p1b7tm',
            prodshareAdEmailUrl: 'https://vons.onelink.me/MuHG/8g1tunmx',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        safeway: {
            banner: 'Safeway',
            loyaltyPgmName: 'Club Card',
            promoDescription: 'Club Card Price',
            estimatedSavings: 'Est. Club Card Savings',
            adaEstimatedSavings: 'Estimated Club Card Savings',
            logoUrls: {
                color: 'Safeway_RGB.svg',
                white: 'Safeway_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://safewayqa.onelink.me/ZdH8/mlsn2fhc',
            nonProdshareAdEmailUrl: 'https://safewayqa.onelink.me/ZdH8/3ob5ujpu',
            prodshareAdCopyUrl: 'https://safeway.onelink.me/kO9l/1wyhfel1',
            prodshareAdEmailUrl: 'https://safeway.onelink.me/kO9l/8d9cotlq',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        randalls: {
            banner: 'Randalls',
            loyaltyPgmName: 'Remarkable Card',
            promoDescription: 'Remarkable Card Price',
            estimatedSavings: 'Est. Remarkable Card Savings',
            adaEstimatedSavings: 'Estimated Remarkable Card Savings',
            logoUrls: {
                color: 'Randalls_RGB.svg',
                white: 'Randalls_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://randallsqa.onelink.me/hA1J/b3z10fgh',
            nonProdshareAdEmailUrl: 'https://randallsqa.onelink.me/hA1J/0kso2sfc',
            prodshareAdCopyUrl: 'https://randalls.onelink.me/H77t/6s7os6q3',
            prodshareAdEmailUrl: 'https://randalls.onelink.me/H77t/krfez08i',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        pavilions: {
            banner: 'Pavilions',
            loyaltyPgmName: 'Club Price',
            promoDescription: 'Club Price',
            estimatedSavings: 'Est. Club Price Savings',
            adaEstimatedSavings: 'Estimated Club Price Savings',
            logoUrls: {
                color: 'Pavilions_RGB.svg',
                white: 'Pavilions_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://pavilionsqa.onelink.me/fZ38/xjhrw4zv',
            nonProdshareAdEmailUrl: 'https://pavilionsqa.onelink.me/fZ38/ktmwv5mm',
            prodshareAdCopyUrl: 'https://pavilions.onelink.me/WHIR/fczqc620',
            prodshareAdEmailUrl: 'https://pavilions.onelink.me/WHIR/411wz9ky',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        jewelosco: {
            banner: 'Jewel-Osco',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            adaEstimatedSavings: 'Estimated Sale Price Savings',
            logoUrls: {
                color: 'JewelOsco_Vert_Oval_RGB.svg',
                white: 'JewelOsco_Vert_Oval_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://jeweloscoqa.onelink.me/pLlJ/l8luopit',
            nonProdshareAdEmailUrl: 'https://jeweloscoqa.onelink.me/pLlJ/a4i1v8w6',
            prodshareAdCopyUrl: 'https://jewelosco.onelink.me/tfDt/r7av1n2i',
            prodshareAdEmailUrl: 'https://jewelosco.onelink.me/tfDt/hljnlsvl',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        carrsqc: {
            banner: 'Carrs',
            loyaltyPgmName: 'Club Card',
            promoDescription: 'Club Card Price',
            estimatedSavings: 'Est. Club Card Savings',
            adaEstimatedSavings: 'Estimated Club Card Savings',
            logoUrls: {
                color: 'Carrs_RGB.svg',
                white: 'Carrs_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://carrsqcqa.onelink.me/6KIe/g6p5zbdv',
            nonProdshareAdEmailUrl: 'https://carrsqcqa.onelink.me/6KIe/5srpu4g7',
            prodshareAdCopyUrl: 'https://carrsqc.onelink.me/TBVv/r64ihprp',
            prodshareAdEmailUrl: 'https://carrsqc.onelink.me/TBVv/9d75bs26',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        acmemarkets: {
            banner: 'Acme',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'Acme_RGB.svg',
                white: 'Acme_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://acmeqa.onelink.me/8H2K/5s8dfcno',
            nonProdshareAdEmailUrl: 'https://acmeqa.onelink.me/8H2K/7glzgizb',
            prodshareAdCopyUrl: 'https://acme.onelink.me/8yDm/28f2radk',
            prodshareAdEmailUrl: 'https://acme.onelink.me/8yDm/55ul1479',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        shaws: {
            banner: 'Shaws',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'Shaws_RGB.svg',
                white: 'Shaws_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://shawsqa.onelink.me/2R1Y/857r9hib',
            nonProdshareAdEmailUrl: 'https://shawsqa.onelink.me/2R1Y/3l5gi97m',
            prodshareAdCopyUrl: 'https://shaws.onelink.me/clvH/yrascv7f',
            prodshareAdEmailUrl: 'https://shaws.onelink.me/clvH/ygvjpu79',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        starmarket: {
            banner: 'Star Market',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'StarMarket_RGB.svg',
                white: 'StarMarket_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://starmarketqa.onelink.me/1Uq8/xfrbxxup',
            nonProdshareAdEmailUrl: 'https://starmarketqa.onelink.me/1Uq8/ynybgzpj',
            prodshareAdCopyUrl: 'https://starmarket.onelink.me/PRuZ/7pb52aft',
            prodshareAdEmailUrl: 'https://starmarket.onelink.me/PRuZ/qhd3898j',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        kingsfoodmarkets: {
            banner: 'Kings Food Markets',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'Kingsfoodmarkets_RGB.svg',
                white: 'Kingsfoodmarkets_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://kingsfoodmarketsqa.onelink.me/K6Mf/co69hi3l',
            nonProdshareAdEmailUrl: 'https://kingsfoodmarketsqa.onelink.me/K6Mf/6d0yqya5',
            prodshareAdCopyUrl: 'https://kingsfoodmarkets.onelink.me/pJ68/oj2vurxd',
            prodshareAdEmailUrl: 'https://kingsfoodmarkets.onelink.me/pJ68/p484dtj9',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        haggen: {
            banner: 'Haggen',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'haggen_RGB.svg',
                white: 'haggen_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://haggenqa.onelink.me/oIah/134zk27d',
            nonProdshareAdEmailUrl: 'https://haggenqa.onelink.me/oIah/psrpflri',
            prodshareAdCopyUrl: 'https://haggen.onelink.me/kuEK/wjhsrunw',
            prodshareAdEmailUrl: 'https://haggen.onelink.me/kuEK/qskz4zhg',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        andronicos: {
            banner: 'Andronico\'s Community Markets',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'andronicos_RGB.svg',
                white: 'andronicos_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://andronicosqa.onelink.me/aRqI/vcaf5lnm',
            nonProdshareAdEmailUrl: 'https://andronicosqa.onelink.me/aRqI/h409nwkz',
            prodshareAdCopyUrl: 'https://andronicos.onelink.me/qfVl/rr7ea69o',
            prodshareAdEmailUrl: 'https://andronicos.onelink.me/qfVl/06w5gp9g',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        balduccis: {
            banner: 'Balducci\'s',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'balduccis_RGB.svg',
                white: 'balduccis_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://balduccisqa.onelink.me/AanP/swqyatm1',
            nonProdshareAdEmailUrl: 'https://balduccisqa.onelink.me/AanP/u7l7dcxb',
            prodshareAdCopyUrl: 'https://balduccis.onelink.me/s5Cs/93aiyyie',
            prodshareAdEmailUrl: 'https://balduccis.onelink.me/s5Cs/ybh7oq4i',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html?rand=' + Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
        },
        shopamigos: {
            banner: 'shopamigos',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'shopamigos_RGB.svg',
                white: 'shopamigos_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://shopamigosqa.onelink.me/AanP/swqyatm1',
            nonProdshareAdEmailUrl: 'https://shopamigosqa.onelink.me/AanP/u7l7dcxb',
            prodshareAdCopyUrl: 'https://shopamigos.onelink.me/s5Cs/93aiyyie',
            prodshareAdEmailUrl: 'https://shopamigos.onelink.me/s5Cs/ybh7oq4i',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html'
        },

        shopmarketstreet:{
            banner: 'Market Street',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'shopmarketstreet_RGB.svg',
                white: 'shopmarketstreet_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://shopmarketstreetqa.onelink.me/AanP/swqyatm1',
            nonProdshareAdEmailUrl: 'https://shopmarketstreetqa.onelink.me/AanP/u7l7dcxb',
            prodshareAdCopyUrl: 'https://shopmarketstreetqa.onelink.me/s5Cs/93aiyyie',
            prodshareAdEmailUrl: 'https://shopmarketstreetqa.onelink.me/s5Cs/ybh7oq4i',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html'
        },
        shopunitedsupermarkets:{
            banner: 'United Supermarkets',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'shopunitedsupermarkets_RGB.svg',
                white: 'shopunitedsupermarkets_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://shopunitedsupermarketsqa.onelink.me/AanP/swqyatm1',
            nonProdshareAdEmailUrl: 'https://shopunitedsupermarketsqa.onelink.me/AanP/u7l7dcxb',
            prodshareAdCopyUrl: 'https://shopunitedsupermarketsqa.onelink.me/s5Cs/93aiyyie',
            prodshareAdEmailUrl: 'https://shopunitedsupermarketsqa.onelink.me/s5Cs/ybh7oq4i',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html'
        },
        shopalbertsonsmarket:{
            banner: 'Albertsons Market',
            loyaltyPgmName: 'Sale Price',
            promoDescription: 'Sale Price',
            estimatedSavings: 'Est. Sale Price Savings',
            logoUrls: {
                color: 'shopalbertsonsmarket_RGB.svg',
                white: 'shopalbertsonsmarket_W.svg'
            },
            nonProdshareAdCopyUrl: 'https://shopalbertsonsmarketqa.onelink.me/AanP/swqyatm1',
            nonProdshareAdEmailUrl: 'https://shopalbertsonsmarketqa.onelink.me/AanP/u7l7dcxb',
            prodshareAdCopyUrl: 'https://shopalbertsonsmarket.onelink.me/s5Cs/93aiyyie',
            prodshareAdEmailUrl: 'https://shopalbertsonsmarket.onelink.me/s5Cs/ybh7oq4i',
            leftNavigationUrl: '/content/experience-fragments/www/for_u/navigation/{banner}.content.html'
        }
    },
    PromoTypeDescription: {
        BOGO: 'Buy 1 Get 1 Free',
        B2G1: 'Buy Two Get 1 Free'
    },
    timer: {
        lastLoginAllowedTime: 45
    },
    summaryTexts: {
        sumamrySnapText: 'SNAP Eligible Item Total',
        sumamryNonSnapText: 'Non-SNAP Eligible Item Total'
    },
    bannerMapper: {
        safeway: 'SAFEWAY',
        vons: 'VONS',
        randalls: 'RANDALLS',
        tomthumb: 'TOMTHUMB',
        acmemarkets: 'ACMEMARKETS',
        carrsqc: 'CARRSQC',
        jewelosco: 'JEWELOSCO',
        albertsons: 'ALBERTSONS',
        starmarket: 'STARMARKET',
        shaws: 'SHAWS',
        pavilions: 'PAVILIONS'
    },
    bannerLogoMapper: {
        safeway: '/content/dam/logos/Safeway_RGB.svg',
        vons: '/content/dam/logos/Vons_RGB.svg',
        randalls: '/content/dam/safeway/images/logos/Randalls_RGB.svg',
        tomthumb: '/content/dam/safeway/images/logos/TomThumb_RGB.svg',
        acmemarkets: '/content/dam/logos/Acme_RGB.svg',
        carrsqc: '/content/dam/logos/Carrs_RGB.svg',
        jewelosco: '/content/dam/logos/JewelOsco_Vert_Oval_RGB.svg',
        albertsons: '/content/dam/safeway/images/logos/Albertsons_RGB.svg',
        starmarket: '/content/dam/logos/StarMarket_RGB.svg',
        shaws: '/content/dam/logos/Shaws_RGB.svg',
        pavilions: '/content/dam/logos/Pavilions_RGB.svg'
    },
    invalidReasonCode: "Cancellation reason is invalid.",
    cancelDuringBatchProcess: "Subscription is renewing and cannot be cancelled.",
    cancelWithinBufferTime: "Your plan is currently being renewed and cannot be cancelled at this time.",
    serverError: "Internal server error occured.",
    unknownError: "Unknown error. Please contact the support team for resolution.",
    subsPaymentError: "Your enrollment cannot be processed. Please update your payment method and try again.",
    subsDefaultError: "Sorry, we\'re having technical difficulties and working on fixing them, please check back later.",
    cardUnacceptedError: "Card cannot be accepted. Please confirm card details were entered correctly or continue with another form of payment",
    freshpassBenefits: {
        "freshpass_tile_item__854420891": {
            "fpImage": "/freshpass/dist/assets/images/greenBenefitsTruck.svg",
            "rteTitle": "<div style=\"text-align: left;\">Unlimited Free Delivery on orders over $30<\/div>\r\n",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "benefitsTruck"
        },
        "freshpass_tile_item__1519499692": {
            "fpImage": "/freshpass/dist/assets/images/monthlycredit.svg",
            "rteTitle": "$5 Monthly Credit for Annual Subscribers",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "$5 monthly credit for annual subscribers"
        },
        "freshpass_tile_item_": {
            "fpImage": "/freshpass/dist/assets/images/save20.svg",
            "rteTitle": "Save $20 Off Your First Online Order Over $75",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "save 20 image"
        },
        "freshpass_tile_item__797202485": {
            "fpImage": "/freshpass/dist/assets/images/2xpoints.svg",
            "rteTitle": "<div style=\"text-align: left;\">Earn 2X Reward Points on Our Exclusive Brands<\/div>\r\n",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "Earn 2X Reward Points on Our Exclusive Brands"
        },
        "freshpass_tile_item__566169671": {
            "fpImage": "/freshpass/dist/assets/images/organic.svg",
            "rteTitle": "Save 5% on O Organics<sup>®<\/sup> and Open Nature<sup>®<\/sup>",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "Save 5% on O Organics and Open Nature"
        },
        "freshpass_tile_item__1650940289": {
            "fpImage": "/freshpass/dist/assets/images/pet.svg",
            "rteTitle": "Save 5% on Signature Pet Care<sup>®<\/sup>",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "Save 5% on Signature Pet Care"
        },
        "freshpass_tile_item__1951713976": {
            "fpImage": "/freshpass/dist/assets/images/rewardsDontExpire.svg",
            "rteTitle": "Rewards Don't Expire",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "Rewards don't expire"
        },
        "freshpass_tile_item__1555375445": {
            "fpImage": "/freshpass/dist/assets/images/starbucksCupArt.svg",
            "rteTitle": "Monthly Exclusive Starbucks<sup>®<\/sup> Perks",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "starbucks cup"
        },
        "freshpass_tile_item__1944760550": {
            "fpImage": "/freshpass/dist/assets/images/vipPhoneLine.svg",
            "rteTitle": "VIP Phone Line",
            "rule": "static",
            "infoIcon": "false",
            "textIsRich": "true",
            "tileImageAltText": "vip phone line"
        },
    },
    benefits_DUG5: {
        acme:  {
            "freshpass_tile_item__1": {
                "fpImage": "/freshpass/dist/assets/images/5OffDUG.svg",
                "rteTitle": "5% off Drive Up & Go™ orders** Ends 8/15",
                "rule": "static",
                "infoIcon": "false",
                "textIsRich": "true",
                "tileImageAltText": "drive up and go image"
            },
        },
        jewelosco: {
            "freshpass_tile_item__2": {
                "fpImage": "/freshpass/dist/assets/images/5OffDUG.svg",
                "rteTitle": "5% off Drive Up & Go™ orders** Ends 6/15",
                "rule": "static",
                "infoIcon": "false",
                "textIsRich": "true",
                "tileImageAltText": "drive up and go image"
            },
        }
    },
    
    snackbar: {
        discountAndActive: {
            message:"We’re sorry, this promotion is only available for new FreshPass subscribers.",
            type:"error",
            show: true,
            closeOption:true,
            from:"discountCampaignAndActiveMembership",
            timer: 5000,
            closeOnClick: true,
            theme:"black",
            topVH: "12vh",
            leftVH: '80vw',
        }
    },
    featureFlags: {
      enableOldWalletFlow: false,
      enableEmployeeDiscount: false,
      enableWallet: true,
      freshpassRebranding: false,

      // Discovery page component visibility flags
      enableFlexModule: true,
      enableFlexModuleCarousel: true,
      enableBundlesCarousel: true,
      enableCouponGallery: true,
      enableWeeklyAdCarousel: true,
      enableMoreSavings: true,
      enableShopByCategory: true,
      enableProductCarousel: true,
      enableSponsoredCarousel: true,
    },
    banner_unit_props: {
        topCarousel: {
            pageName: 'coupons-deals',
            position: 'top',
            id: 'coupons-deals-top-carosuel',
            attribute: 'data-content-top-carousel'
        },
        sideBanner: {
            pageName: 'coupons-deals',
            position: 'side',
            id: 'coupons-deals-side-banner',
            attribute: 'data-content-side-banner'
        },
        bottomNonEndemic: {
            pageName: 'coupons-deals',
            position: 'bottom',
            id: 'coupons-deals-bottom-non-endemic',
            attribute: 'data-content-bottom-banner'
        }
    },
    displayableBannerNames: {
        safeway: 'Safeway',
        vons: 'Vons',
        albertsons: 'Albertsons',
        shaws: 'Shaw\'s',
        andronicos: 'Andronico\'s',
        jewelosco: 'Jewel-Osco',
        acmemarkets: 'ACME Markets',
        starmarket: 'Star Market',
        carrsqc: 'Carrs',
        randalls: 'Randalls',
        tomthumb: 'Tom Thumb',
        pavilions: 'Pavilions',
        haggen: 'Haggen',
        kingsfoodmarkets: 'Kings Food Markets',
        balduccis: 'Balducci\'s',
        shopamigos: 'Amigos',
        shopmarketstreet:'Market Street',
        shopunitedsupermarkets:'United Supermarkets',
        shopalbertsonsmarket:'Albertsons Market'
    },
    membershipBenefitsConfig: {
        fragment: 'foru-guest',
        mandatoryZones: ['zone0', 'zone1', 'zone2']
    },
};
